import type { AppointmentHandoff } from './appointment-handoff';
import {
  appointmentOfferSchema,
  appointmentSelectionSchema,
  assertAppointmentExpression,
  appointmentPreferenceSchema,
  buyerAppointmentPreference,
} from './appointment-selection';
import { z } from 'zod';
import { AssistantError } from './assistant';
import type { ConversationMessage, SourceFact, SalesSignal } from './assistant';
import type { SalesJourney } from './sales';
import { propertySearchSchema } from './catalog';
import {
  adviserDecisionSchema,
  buyerEvidenceSchema,
  salesAnswerCoverageSchema,
  salesArgumentSchema,
  salesTurnUnderstandingSchema,
  type SalesAnswerCoverage,
  type SalesTurnUnderstanding,
} from './sales-turn';

const label = z.string().trim().min(1).max(100);
const description = z.string().trim().min(1).max(600);
export const salesTechniqueIdSchema = z.enum([
  'adaptive_listening',
  'personal_imagery',
  'self_persuasion',
  'meaningful_comparison',
  'objection_resolution',
  'credible_social_proof',
  'real_opportunity',
  'concrete_planning',
]);
export type SalesTechniqueId = z.infer<typeof salesTechniqueIdSchema>;
export const salesPlaybookSchema = z
  .object({
    version: z.literal(1),
    goal: z.enum(['qualified_lead', 'wanted_sales_appointment']),
    positioning: description,
    motivations: z
      .array(z.object({ id: label, description }).strict())
      .min(1)
      .max(12),
    benefits: z
      .array(
        z
          .object({
            id: label,
            motivationIds: z.array(label).min(1).max(12),
            source: z.string().min(1).max(2000),
            evidence: description,
            meaning: description,
            kind: z.enum(['feature', 'social_proof', 'scarcity']),
          })
          .strict()
      )
      .max(30),
    concerns: z.array(z.object({ id: label, description }).strict()).max(12),
    adviserOutcomes: z
      .array(
        z
          .object({
            id: label,
            motivationIds: z.array(label).max(12),
            concernIds: z.array(label).max(12),
            description,
          })
          .strict()
      )
      .min(1)
      .max(12),
    enabledTechniques: z.array(salesTechniqueIdSchema).min(1).max(8),
    excludedClaims: z.array(description).max(12),
  })
  .strict()
  .superRefine((playbook, ctx) => {
    const motives = new Set(playbook.motivations.map((item) => item.id));
    const concerns = new Set(playbook.concerns.map((item) => item.id));
    for (const group of [
      playbook.motivations,
      playbook.benefits,
      playbook.concerns,
      playbook.adviserOutcomes,
    ]) {
      if (new Set(group.map((item) => item.id)).size !== group.length)
        ctx.addIssue({
          code: 'custom',
          message: 'Sales playbook IDs must be unique within each collection.',
        });
    }
    for (const item of [...playbook.benefits, ...playbook.adviserOutcomes])
      if (item.motivationIds.some((id) => !motives.has(id)))
        ctx.addIssue({
          code: 'custom',
          message: 'Sales benefit or outcome references an unknown motivation.',
        });
    for (const item of playbook.adviserOutcomes)
      if (item.concernIds.some((id) => !concerns.has(id)))
        ctx.addIssue({
          code: 'custom',
          message: 'Adviser outcome references an unknown concern.',
        });
  });
export type SalesPlaybook = z.infer<typeof salesPlaybookSchema>;

export const buyerSalesSignalsSchema = z
  .object({
    motivationId: label.nullable(),
    concernId: label.nullable(),
    singleFact: z.boolean(),
    readiness: z.enum([
      'unknown',
      'exploring',
      'interested',
      'requested',
      'declined',
    ]),
    responseToApproach: z.enum([
      'unknown',
      'engaged',
      'resisted',
      'corrected',
      'neutral',
    ]),
  })
  .strict();
export type BuyerSalesSignals = z.infer<typeof buyerSalesSignalsSchema>;
const contactValueSchema = z
  .object({
    value: z.string().trim().min(1).max(254),
    evidence: buyerEvidenceSchema,
  })
  .strict();
export const buyerContactPatchSchema = z
  .object({
    firstName: contactValueSchema.nullable(),
    lastName: contactValueSchema.nullable(),
    phone: contactValueSchema.nullable(),
    email: contactValueSchema.nullable(),
  })
  .strict();
const storedContactValue = contactValueSchema.extend({
  occurredAt: z.iso.datetime({ offset: true }),
});
const buyerContactSchema = z
  .object({
    firstName: storedContactValue.optional(),
    lastName: storedContactValue.optional(),
    phone: storedContactValue.optional(),
    email: storedContactValue.optional(),
  })
  .strict();
const observationSchema = z
  .object({
    key: label,
    kind: z.enum([
      'desired_outcome',
      'priority',
      'hesitation',
      'decision',
      'adviser_agenda',
    ]),
    value: description,
    status: z.enum(['stated', 'tentative', 'resolved']),
    evidence: buyerEvidenceSchema,
  })
  .strict();
export const buyerContextPatchSchema = z
  .object({
    observations: z.array(observationSchema).max(12),
    removals: z
      .array(z.object({ key: label, evidence: buyerEvidenceSchema }).strict())
      .max(12),
    contact: buyerContactPatchSchema.nullable().optional(),
  })
  .strict();
export type BuyerContextPatch = z.infer<typeof buyerContextPatchSchema>;
const objectiveSchema = z.enum([
  'answer_only',
  'discover_motivation',
  'build_desire',
  'clarify_fit',
  'resolve_hesitation',
  'earn_call',
  'complete_scheduling',
]);
const nextStepSchema = z.enum([
  'none',
  'useful_question',
  'invitation',
  'lead_capture',
  'missing_contact',
  'scheduling',
]);
const salesInvitationSchema = z
  .object({
    reason: z.enum([
      'positive_interest',
      'specific_hesitation',
      'appointment_value',
      'renewed_interest',
    ]),
    adviserOutcomeId: label,
  })
  .strict();
type SalesInvitation = z.infer<typeof salesInvitationSchema>;
export const salesTurnPlanSchema = z
  .object({
    objective: objectiveSchema,
    eligibleTechniqueIds: z.array(salesTechniqueIdSchema).max(3),
    buyerEvidenceRefs: z.array(label).max(30),
    factRefs: z.array(z.string().min(1).max(500)).max(30),
    targetConcern: description.nullable(),
    callValue: description.nullable(),
    nextStep: nextStepSchema,
    invitation: salesInvitationSchema.optional(),
    avoidRepeating: z.array(z.string().max(600)).max(16),
    understanding: salesTurnUnderstandingSchema.optional(),
    argument: salesArgumentSchema.optional(),
    adviserDecision: adviserDecisionSchema.optional(),
    offeringIds: z.array(z.string().min(1).max(500)).max(30).optional(),
    answers: salesAnswerCoverageSchema.optional(),
  })
  .strict();
export type SalesTurnPlan = z.infer<typeof salesTurnPlanSchema>;
export const buyerSalesContextSchema = z
  .object({
    version: z.literal(1),
    observations: z.array(observationSchema).max(48),
    motivationId: label.nullable(),
    concernId: label.nullable(),
    adviserDecision: adviserDecisionSchema.optional(),
    contact: buyerContactSchema.optional(),
    appointmentSelection: appointmentSelectionSchema.nullable().optional(),
    appointmentAuthorization: appointmentSelectionSchema
      .pick({
        connectionId: true,
        calendarId: true,
        poolId: true,
        serviceId: true,
        startsAt: true,
        endsAt: true,
        evidence: true,
      })
      .nullable()
      .optional(),
    appointmentPreference: appointmentPreferenceSchema.nullable().optional(),
    appointmentOffer: appointmentOfferSchema.nullable().optional(),
    propertySearch: propertySearchSchema.nullable().optional(),
    invitationResponse: salesTurnUnderstandingSchema.shape.invitationResponse,
    appointmentIntent: z.enum([
      'unmentioned',
      'offered',
      'deferred',
      'requested',
      'declined',
      'arranging',
      'booked',
    ]),
    approaches: z
      .array(
        z
          .object({
            eventId: label,
            outboxId: label,
            techniqueId: salesTechniqueIdSchema.nullable(),
            objective: objectiveSchema,
            nextStep: nextStepSchema,
            callValue: description.nullable(),
            replyExcerpt: z.string().max(600),
            delivery: z.enum(['pending', 'delivered', 'failed']),
            argument: salesArgumentSchema.optional(),
            adviserOutcomeId: label.optional(),
            buyerEvidenceRefs: z.array(label).max(30).optional(),
          })
          .strict()
      )
      .max(8),
  })
  .strict();
export type BuyerSalesContext = z.infer<typeof buyerSalesContextSchema>;
export function rememberSalesApproach(
  buyer: BuyerSalesContext,
  approach: BuyerSalesContext['approaches'][number]
): BuyerSalesContext {
  return buyerSalesContextSchema.parse({
    ...buyer,
    appointmentIntent:
      buyer.appointmentIntent === 'unmentioned' &&
      approach.delivery === 'delivered' &&
      approach.nextStep === 'invitation'
        ? 'offered'
        : buyer.appointmentIntent,
    approaches: [
      ...buyer.approaches.filter((item) => item.eventId !== approach.eventId),
      approach,
    ].slice(-8),
  });
}
export function emptyBuyerSalesContext(): BuyerSalesContext {
  return {
    version: 1,
    observations: [],
    motivationId: null,
    concernId: null,
    appointmentIntent: 'unmentioned',
    approaches: [],
  };
}
export const storedSalesTurnSchema = z
  .object({
    version: z.literal(1),
    buyer: buyerSalesContextSchema,
    plan: salesTurnPlanSchema,
    selectedTechniqueId: salesTechniqueIdSchema.nullable(),
  })
  .strict();
export type StoredSalesTurn = z.infer<typeof storedSalesTurnSchema>;

export interface SalesTechnique {
  id: SalesTechniqueId;
  mechanism: string;
  research: readonly string[];
  applicableWhen: string;
  evidenceRequired: string;
  responseObjective: string;
  changeOrStopWhen: string;
  exampleApproach: string;
}
const listening =
  'https://www.sciencedirect.com/science/article/pii/S0148296319303017';
export const SALES_TECHNIQUES: readonly SalesTechnique[] = [
  {
    id: 'adaptive_listening',
    mechanism: 'Personal relevance through responsive listening',
    research: [
      listening,
      'https://journals.sagepub.com/doi/10.1509/jmkr.43.4.693',
    ],
    applicableWhen: 'Buyer volunteers a goal or corrects our understanding.',
    evidenceRequired:
      'Actual buyer statement; sources for any product argument.',
    responseObjective:
      'Show understanding through a recommendation that changes with the buyer.',
    changeOrStopWhen:
      'Do not echo the same aspiration repeatedly; update after correction.',
    exampleApproach:
      'Explain the same offering differently for weekend use and everyday living.',
  },
  {
    id: 'personal_imagery',
    mechanism: 'Self-referencing and mental simulation',
    research: [
      'https://academic.oup.com/jcr/article-abstract/33/4/421/1790292',
    ],
    applicableWhen: 'An expressed aspiration matches a supported feature.',
    evidenceRequired:
      'Buyer aspiration and a current matching source-linked benefit.',
    responseObjective:
      'Make one personally meaningful use tangible and desirable.',
    changeOrStopWhen:
      'Buyer wants analytical detail, resists the imagery or requests the appointment.',
    exampleApproach:
      'Relate a verified location to the buyer’s stated weekend plans; do not invent a view.',
  },
  {
    id: 'self_persuasion',
    mechanism: 'Buyer articulates personally persuasive reasons',
    research: [
      'https://www.sciencedirect.com/org/science/article/pii/S0309056617000880',
    ],
    applicableWhen:
      'Buyer explores but what makes ownership worthwhile is unclear.',
    evidenceRequired:
      'Their question or volunteered priority; never a fabricated motive.',
    responseObjective:
      'Ask one easy question whose answer changes the recommendation.',
    changeOrStopWhen:
      'The motive is known, the buyer declines questions, or wants to arrange the call.',
    exampleApproach:
      'Explore what they most want to enjoy there, rather than run a qualification checklist.',
  },
  {
    id: 'meaningful_comparison',
    mechanism: 'Reduce decision effort through relevant tradeoffs',
    research: [
      'https://www.sciencedirect.com/science/article/abs/pii/S1057740814000916',
    ],
    applicableWhen: 'Buyer compares or struggles to choose.',
    evidenceRequired: 'Current offering facts for each compared alternative.',
    responseObjective:
      'Explain a small relevant comparison using the buyer’s priorities.',
    changeOrStopWhen:
      'Buyer chooses, changes priorities or requests broader options.',
    exampleApproach:
      'Compare the dimensions that matter to this buyer rather than every specification.',
  },
  {
    id: 'objection_resolution',
    mechanism: 'Adaptive response to the actual barrier',
    research: [listening],
    applicableWhen:
      'Buyer expresses a concern about fit, price, trust, timing or the call.',
    evidenceRequired:
      'Their concern and sources for any answer; ask only if ambiguity changes the answer.',
    responseObjective:
      'Resolve or precisely narrow the hesitation and make the next decision easier.',
    changeOrStopWhen:
      'Concern is resolved or corrected; never repeat an ineffective counterargument.',
    exampleApproach:
      'Distinguish total budget from uncertainty about value before responding.',
  },
  {
    id: 'credible_social_proof',
    mechanism: 'Relevant documented experience reduces uncertainty',
    research: [
      'https://sparq.stanford.edu/sites/g/files/sbiybj19021/files/media/file/goldstein_et_al._2008_-_a_room_with_a_viewpoint.pdf',
    ],
    applicableWhen:
      'A relevant approved example or aggregate supports this buyer’s concern.',
    evidenceRequired:
      'Matching source-linked social proof; never infer popularity from inventory.',
    responseObjective: 'Help evaluate the offer with relevant evidence.',
    changeOrStopWhen:
      'No directly applicable evidence or buyer asks about their own specific fit.',
    exampleApproach:
      'Use an actual applicable customer experience without suggesting everyone is buying.',
  },
  {
    id: 'real_opportunity',
    mechanism: 'Salience of a genuinely limited opportunity',
    research: ['https://doi.org/10.1016/j.jretai.2022.06.003'],
    applicableWhen:
      'A documented current constraint affects a buyer’s expressed preference.',
    evidenceRequired:
      'Matching source-linked scarcity observed within five minutes.',
    responseObjective:
      'Explain the real consequence for this choice, without manufactured urgency.',
    changeOrStopWhen: 'Evidence expires, is unrelated, or the buyer declines.',
    exampleApproach:
      'Mention a verified relevant deadline; never turn a short result page into scarcity.',
  },
  {
    id: 'concrete_planning',
    mechanism: 'Translate intention into a specific next action',
    research: ['https://pmc.ncbi.nlm.nih.gov/articles/PMC3127912/'],
    applicableWhen: 'Buyer requests the appointment or is arranging it.',
    evidenceRequired:
      'Buyer intent, actual calendar slots, supplied contacts and action receipts.',
    responseObjective:
      'Complete the chosen appointment with minimal conversational effort.',
    changeOrStopWhen: 'Buyer withdraws or appointment is confirmed.',
    exampleApproach:
      'Act on “Friday at three”; ask only for missing information.',
  },
];

export interface SalesPsychologyInput {
  buyer: BuyerSalesContext;
  signals: BuyerSalesSignals | undefined;
  signal: SalesSignal | undefined;
  messages: readonly ConversationMessage[];
  facts: readonly SourceFact[];
  playbook: SalesPlaybook;
  journey: SalesJourney | null;
  now: string;
  understanding?: SalesTurnUnderstanding | undefined;
  // The classifier read the message as contact details and nothing else.
  contactOnly?: boolean | undefined;
}
export interface SalesReplyBrief {
  plan: SalesTurnPlan;
  selectedTechniqueId: SalesTechniqueId | null;
  technique: SalesTechnique | null;
  supportedBenefits: ReturnType<typeof supportedSalesBenefits>;
  appointment: AppointmentHandoff | null;
}

const normalize = (text: string) =>
  text.normalize('NFKC').toLocaleLowerCase().replace(/\s+/gu, ' ').trim();
export function supportedSalesBenefits(
  input: Pick<SalesPsychologyInput, 'facts' | 'playbook' | 'now'>
) {
  return input.playbook.benefits.flatMap((benefit) => {
    const fact = input.facts.find(
      (item) =>
        !item.private &&
        (item.source === benefit.source || item.id === benefit.source) &&
        normalize(item.content).includes(normalize(benefit.evidence))
    );
    if (!fact) return [];
    if (
      benefit.kind === 'scarcity' &&
      (Date.parse(input.now) - Date.parse(fact.observedAt) > 300_000 ||
        Date.parse(fact.observedAt) > Date.parse(input.now))
    )
      return [];
    return [{ ...benefit, factId: fact.id }];
  });
}

export function isOfferingFact(fact: SourceFact): boolean {
  if (fact.private) return false;
  try {
    const value: unknown = JSON.parse(fact.content);
    return (
      value !== null &&
      typeof value === 'object' &&
      ('code' in value || 'id' in value) &&
      ('title' in value || 'name' in value)
    );
  } catch {
    return false;
  }
}

// The buyer was asked for a time and their details once. A plain question
// after that is answered without asking again, until they return to the call
// by naming a time, sending details or asking for it.
export function schedulingAskSetAside(input: {
  buyer: BuyerSalesContext | undefined;
  signal: SalesSignal | undefined;
  text: string;
  contactOnly?: boolean | undefined;
}): boolean {
  return (
    Boolean(
      input.buyer?.approaches.some(
        (item) =>
          item.delivery === 'delivered' &&
          ['scheduling', 'missing_contact'].includes(item.nextStep)
      )
    ) &&
    // "other" is where the classifier files a change of need or a remark. A
    // request for the call or a confirmation of it is never set aside.
    [
      'factual_question',
      'exploration',
      'comparison',
      'objection',
      'other',
    ].includes(input.signal ?? '') &&
    // Contact details and a time are what the ask was for.
    !input.contactOnly &&
    !/[^\s@]+@[^\s@]+\.[^\s@]+|(?:\d[\s()-]?){7,}/u.test(input.text) &&
    !buyerAppointmentPreference(input.text)
  );
}

export class SalesPsychologyEngine {
  understandBuyer(
    input: SalesPsychologyInput,
    patch: BuyerContextPatch,
    understanding?: SalesTurnUnderstanding
  ): BuyerSalesContext {
    const latestMessage = input.messages
      .filter((message) => message.role === 'customer')
      .at(-1);
    if (latestMessage)
      assertAppointmentExpression(
        latestMessage,
        understanding?.appointmentExpression
      );
    const storedContact = Object.values(input.buyer.contact ?? {}).filter(
      (item) => item !== undefined
    );
    const assertEvidence = (evidence: z.infer<typeof buyerEvidenceSchema>) => {
      const message = input.messages.find(
        (item) => item.id === evidence.messageId && item.role === 'customer'
      );
      const saved = [...input.buyer.observations, ...storedContact].filter(
        (item) => item.evidence.messageId === evidence.messageId
      );
      if (
        !normalize(message?.text ?? '').includes(normalize(evidence.quote)) &&
        !saved.some((item) =>
          normalize(item.evidence.quote).includes(normalize(evidence.quote))
        )
      )
        throw new AssistantError('SALES_BUYER_EVIDENCE_INVALID', 502);
    };
    const observations = new Map(
      input.buyer.observations.map((item) => [item.key, item])
    );
    for (const removal of patch.removals) {
      assertEvidence(removal.evidence);
      observations.delete(removal.key);
    }
    for (const observation of patch.observations) {
      assertEvidence(observation.evidence);
      observations.set(observation.key, observation);
    }
    if (observations.size > 48)
      throw new AssistantError('SALES_CONTEXT_LIMIT_EXCEEDED', 413);
    for (const question of understanding?.questions ?? [])
      assertEvidence(question);
    const contact = { ...input.buyer.contact };
    for (const field of ['firstName', 'lastName', 'phone', 'email'] as const) {
      const entry = patch.contact?.[field];
      if (!entry) continue;
      assertEvidence(entry.evidence);
      const value =
        field === 'phone'
          ? entry.value.replace(/\D/gu, '').replace(/^51(?=9\d{8}$)/u, '')
          : normalize(entry.value);
      const quote =
        field === 'phone'
          ? entry.evidence.quote.replace(/\D/gu, '')
          : normalize(entry.evidence.quote);
      if (
        !quote.includes(value) ||
        (field === 'email' && !z.email().safeParse(entry.value).success) ||
        (field === 'phone' &&
          !/^(?:9(?:[\s().-]*\d){8}|\+[1-9](?:[\s().-]*\d){6,14})$/u.test(
            entry.value
          ))
      )
        throw new AssistantError('SALES_CONTACT_EVIDENCE_INVALID', 502);
      const occurredAt =
        input.messages.find(
          (message) => message.id === entry.evidence.messageId
        )?.occurredAt ??
        storedContact.find(
          (prior) => prior.evidence.messageId === entry.evidence.messageId
        )?.occurredAt;
      if (!occurredAt)
        throw new AssistantError('SALES_CONTACT_EVIDENCE_INVALID', 502);
      contact[field] = { ...entry, occurredAt };
    }
    if (understanding?.invitationResponse)
      assertEvidence(understanding.invitationResponse.evidence);
    const meanings = [
      [understanding?.motivation, input.playbook.motivations],
      [understanding?.concern, input.playbook.concerns],
    ] as const;
    for (const [meaning, choices] of meanings) {
      if (!meaning) continue;
      assertEvidence(meaning.evidence);
      if (!choices.some((choice) => choice.id === meaning.id))
        throw new AssistantError('SALES_BUYER_MEANING_INVALID', 502);
    }
    const unresolved = [...observations.values()].filter(
      (item) => item.kind === 'hesitation' && item.status !== 'resolved'
    );
    const resolved = [...observations.values()].some(
      (item) => item.kind === 'hesitation' && item.status === 'resolved'
    );
    return buyerSalesContextSchema.parse({
      ...input.buyer,
      contact,
      ...(understanding?.invitationResponse
        ? { invitationResponse: understanding.invitationResponse }
        : {}),
      observations: [...observations.values()],
      motivationId: understanding?.motivation?.id ?? input.buyer.motivationId,
      concernId:
        resolved && !unresolved.length && !understanding?.concern
          ? null
          : (understanding?.concern?.id ??
            input.signals?.concernId ??
            input.buyer.concernId),
    });
  }

  planTurn(input: SalesPsychologyInput): SalesTurnPlan {
    const { buyer, signals, signal, playbook, journey, understanding } = input;
    const focus = understanding?.focus;
    const motivationId =
      understanding?.motivation?.id ??
      signals?.motivationId ??
      buyer.motivationId;
    const concernId =
      understanding?.concern?.id ?? signals?.concernId ?? buyer.concernId;
    const unresolved = buyer.observations.filter(
      (item) => item.kind === 'hesitation' && item.status !== 'resolved'
    );
    const currentMessageId = input.messages
      .filter((message) => message.role === 'customer')
      .at(-1)?.id;
    const currentHesitation = unresolved.some(
      (item) =>
        item.status === 'stated' && item.evidence.messageId === currentMessageId
    );
    const previous = buyer.approaches.filter(
      (item) => item.delivery === 'delivered'
    );
    const last = previous.at(-1);
    const disposition = understanding?.invitationResponse?.disposition;
    const refusal =
      disposition === 'declined' ||
      disposition === 'not_now' ||
      focus === 'decline' ||
      (!understanding &&
        (signal === 'appointment_refusal' ||
          signals?.readiness === 'declined'));
    // Readiness is the buyer's standing through the conversation. Once they
    // were asked for a time, a plain question is not another request for it.
    const setAside = schedulingAskSetAside({
      buyer,
      signal,
      text:
        input.messages.filter((message) => message.role === 'customer').at(-1)
          ?.text ?? '',
      contactOnly: input.contactOnly,
    });
    // The writer's readings (the turn's focus, the buyer's answer to the
    // invitation) follow the conversation as well: none of them is a request
    // in a message that only puts a plain question.
    const request =
      !setAside &&
      (disposition === 'requested' ||
        focus === 'arrange' ||
        (!understanding &&
          (signal === 'appointment_request' ||
            signal === 'appointment_confirmation' ||
            signals?.readiness === 'requested')));
    const wantsCall = ['requested', 'arranging'].includes(
      buyer.appointmentIntent
    );
    const arranging = request || (!refusal && wantsCall && !setAside);
    const outcome =
      playbook.adviserOutcomes.find(
        (item) => concernId !== null && item.concernIds.includes(concernId)
      ) ??
      playbook.adviserOutcomes.find(
        (item) =>
          motivationId !== null && item.motivationIds.includes(motivationId)
      ) ??
      playbook.adviserOutcomes.find(
        (item) => item.id === buyer.adviserDecision?.outcomeId
      );
    const benefits = supportedSalesBenefits(input).filter(
      (item) =>
        motivationId !== null && item.motivationIds.includes(motivationId)
    );
    const sources = input.facts.filter((item) => !item.private);
    const offeringFacts = sources.filter(isOfferingFact);
    let objective: SalesTurnPlan['objective'] = 'answer_only';
    let nextStep: SalesTurnPlan['nextStep'] = 'none';
    let techniques: SalesTechniqueId[] = [];
    if (
      !request &&
      (journey?.stage === 'crm_appointment_recorded' ||
        journey?.stage === 'provider_booked' ||
        journey?.stage === 'appointment_cancelled' ||
        journey?.stage === 'appointment_change_pending' ||
        buyer.appointmentIntent === 'booked')
    ) {
      objective = 'answer_only';
    } else if (arranging) {
      objective = 'complete_scheduling';
      nextStep = 'scheduling';
      techniques = ['concrete_planning'];
    } else if (
      refusal &&
      (focus === 'decline' ||
        (!understanding && signal === 'appointment_refusal'))
    ) {
      objective = 'answer_only';
    } else if (
      focus === 'resolve_concern' ||
      focus === 'explain_call' ||
      (focus === 'answer' && currentHesitation) ||
      (!understanding &&
        (signal === 'objection' ||
          (signal === 'factual_question' && signals?.concernId) ||
          (signal === 'other' && (signals?.concernId || unresolved.length))))
    ) {
      objective = focus === 'explain_call' ? 'earn_call' : 'resolve_hesitation';
      techniques = ['objection_resolution', 'adaptive_listening'];
      if (!concernId && !unresolved.length && focus !== 'explain_call')
        nextStep = 'useful_question';
    } else if (
      focus === 'compare' ||
      (!understanding && signal === 'comparison')
    ) {
      objective = 'clarify_fit';
      techniques =
        offeringFacts.length >= 2
          ? ['meaningful_comparison', 'adaptive_listening']
          : ['adaptive_listening'];
      if (!outcome && !refusal && buyer.appointmentIntent !== 'declined')
        nextStep = 'useful_question';
    } else if (
      focus === 'explore' ||
      focus === 'answer' ||
      (!understanding &&
        (signal === 'exploration' ||
          signal === 'factual_question' ||
          signals?.readiness === 'interested' ||
          signals?.readiness === 'exploring' ||
          Boolean(motivationId)))
    ) {
      const hasMotivation =
        motivationId ||
        buyer.observations.some((item) => item.kind === 'desired_outcome');
      objective =
        hasMotivation && (benefits.length || offeringFacts.length)
          ? 'build_desire'
          : 'discover_motivation';
      techniques =
        hasMotivation && (benefits.length || offeringFacts.length)
          ? ['personal_imagery', 'adaptive_listening']
          : ['adaptive_listening', 'self_persuasion'];
      if (
        !hasMotivation ||
        (signals?.readiness === 'exploring' &&
          last?.nextStep !== 'useful_question')
      )
        nextStep = 'useful_question';
    }
    const reopened = disposition === 'reopened' || disposition === 'coordinate';
    // Asking why the call is worthwhile while postponing the booking still
    // invites a useful next step. It never authorizes a calendar mutation.
    const consideringAppointment =
      focus === 'explain_call' && disposition === 'not_now';
    const declined =
      (refusal && !consideringAppointment) ||
      (!reopened && buyer.appointmentIntent === 'declined');
    const lastInvitation = [...previous]
      .reverse()
      .find((item) => item.nextStep === 'invitation');
    const newDecision = buyer.observations.some(
      (item) =>
        item.kind === 'decision' &&
        item.status === 'stated' &&
        item.evidence.messageId === currentMessageId &&
        !(
          lastInvitation?.buyerEvidenceRefs ??
          lastInvitation?.argument?.buyerEvidenceRefs ??
          []
        ).includes(currentMessageId ?? '')
    );
    const currentMotivation =
      Boolean(currentMessageId) &&
      ((!understanding &&
        signals?.motivationId != null &&
        signals.motivationId !== buyer.motivationId) ||
        understanding?.motivation?.evidence.messageId === currentMessageId ||
        buyer.observations.some(
          (item) =>
            item.kind === 'desired_outcome' &&
            item.status === 'stated' &&
            item.evidence.messageId === currentMessageId
        ));
    const positiveInterest =
      (objective === 'clarify_fit' && offeringFacts.length >= 2) ||
      signals?.readiness === 'interested' ||
      currentMotivation ||
      (newDecision && signals?.responseToApproach === 'engaged');
    const specificHesitation =
      objective === 'resolve_hesitation' &&
      Boolean(concernId || currentHesitation);
    const appointmentValue = focus === 'explain_call';
    const currentConcern =
      Boolean(currentMessageId) &&
      (currentHesitation ||
        (!understanding &&
          signals?.concernId != null &&
          signals.concernId !== buyer.concernId) ||
        understanding?.concern?.evidence.messageId === currentMessageId);
    const changedConcern =
      specificHesitation &&
      currentConcern &&
      (lastInvitation?.objective !== 'resolve_hesitation' ||
        ((lastInvitation.adviserOutcomeId ??
        lastInvitation.argument?.adviserOutcomeId)
          ? (lastInvitation.adviserOutcomeId ??
              lastInvitation.argument?.adviserOutcomeId) !== outcome?.id
          : lastInvitation.callValue !== outcome?.description));
    // "Not now" stands like a refusal: only the buyer reopens the call. A new
    // concern or decision is answered on its own merits, without another offer.
    const renewedInterest =
      reopened ||
      (!declined &&
        buyer.appointmentIntent !== 'deferred' &&
        (appointmentValue ||
          changedConcern ||
          (newDecision &&
            positiveInterest &&
            signals?.responseToApproach === 'engaged' &&
            last?.nextStep !== 'invitation')));
    // A call the buyer asked about themselves is already on the table.
    const invited =
      !renewedInterest &&
      (Boolean(lastInvitation) ||
        buyer.appointmentIntent === 'offered' ||
        buyer.appointmentIntent === 'deferred' ||
        wantsCall);
    const invitationReason: SalesInvitation['reason'] | null = reopened
      ? 'renewed_interest'
      : appointmentValue
        ? 'appointment_value'
        : specificHesitation
          ? 'specific_hesitation'
          : positiveInterest
            ? 'positive_interest'
            : null;
    // A known aspiration or specific concern can earn the appointment now;
    // another generic discovery question must not postpone that opportunity.
    if (
      !arranging &&
      !declined &&
      !invited &&
      outcome &&
      invitationReason &&
      objective !== 'answer_only' &&
      (objective !== 'discover_motivation' || reopened)
    ) {
      nextStep =
        playbook.goal === 'wanted_sales_appointment'
          ? 'invitation'
          : 'lead_capture';
      if (
        playbook.goal === 'wanted_sales_appointment' &&
        objective === 'discover_motivation'
      )
        objective = 'earn_call';
    }
    if (!arranging && !declined && objective !== 'answer_only') {
      if (benefits.some((item) => item.kind === 'social_proof'))
        techniques.push('credible_social_proof');
      if (benefits.some((item) => item.kind === 'scarcity'))
        techniques.push('real_opportunity');
    }
    if (signals?.responseToApproach === 'resisted') {
      techniques = techniques.filter(
        (item) => item !== last?.techniqueId || item === 'concrete_planning'
      );
      if (!arranging && !specificHesitation && !appointmentValue && !reopened)
        nextStep = declined ? 'none' : 'useful_question';
    }
    if (signals?.responseToApproach === 'corrected' && !arranging && !refusal) {
      techniques = ['adaptive_listening'];
      nextStep = 'none';
    }
    return {
      objective,
      eligibleTechniqueIds: [...new Set(techniques)]
        .filter((id) => playbook.enabledTechniques.includes(id))
        .slice(0, 3),
      buyerEvidenceRefs: [
        ...new Set([
          ...buyer.observations.map((item) => item.evidence.messageId),
          ...input.messages
            .filter((item) => item.role === 'customer')
            .slice(-6)
            .map((item) => item.id),
        ]),
      ].slice(-30),
      factRefs: [
        ...new Set([
          ...benefits.map((item) => item.factId),
          ...sources.map((item) => item.id),
        ]),
      ].slice(0, 30),
      targetConcern:
        objective === 'resolve_hesitation' || objective === 'earn_call'
          ? (unresolved.at(-1)?.value ??
            playbook.concerns.find((item) => item.id === concernId)
              ?.description ??
            null)
          : null,
      callValue: outcome?.description ?? null,
      nextStep,
      ...(nextStep === 'invitation' && invitationReason && outcome
        ? {
            invitation: {
              reason: invitationReason,
              adviserOutcomeId: outcome.id,
            },
          }
        : {}),
      ...(understanding ? { understanding } : {}),

      avoidRepeating: previous
        .slice(-4)
        .flatMap((item) => [
          item.replyExcerpt,
          ...(item.callValue ? [item.callValue] : []),
        ])
        .slice(-16),
    };
  }

  // This decision is made before generation. The writer receives it as input;
  // it cannot choose a different strategy by returning different metadata.
  prepareReply(
    input: SalesPsychologyInput,
    appointment: AppointmentHandoff | null = null
  ): SalesReplyBrief {
    const plan = this.planTurn(input);
    // The appointment engine takes ownership once arranging or managing a call.
    // Sales classifications cannot restart a pitch or override transaction state.
    if (
      appointment &&
      appointment.state !== 'not_requested' &&
      appointment.state !== 'declined'
    ) {
      const reporting =
        appointment.nextStep === 'report_status' ||
        appointment.nextStep === 'none';
      plan.objective = reporting ? 'answer_only' : 'complete_scheduling';
      plan.nextStep = reporting
        ? 'none'
        : appointment.nextStep === 'confirm_lead'
          ? 'lead_capture'
          : appointment.nextStep === 'ask_missing_contact'
            ? 'missing_contact'
            : 'scheduling';
      plan.eligibleTechniqueIds =
        !reporting &&
        input.playbook.enabledTechniques.includes('concrete_planning')
          ? ['concrete_planning']
          : [];
      delete plan.invitation;
    }
    const selectedTechniqueId = plan.eligibleTechniqueIds[0] ?? null;
    const motivationId =
      input.understanding?.motivation?.id ?? input.buyer.motivationId;
    const supportedBenefits = supportedSalesBenefits(input).filter(
      (benefit) =>
        motivationId !== null && benefit.motivationIds.includes(motivationId)
    );
    const outcome = input.playbook.adviserOutcomes.find(
      (item) => item.id === plan.invitation?.adviserOutcomeId
    );
    if (outcome) {
      const agenda = input.buyer.observations
        .filter(
          (item) =>
            item.status !== 'resolved' &&
            ['desired_outcome', 'hesitation', 'adviser_agenda'].includes(
              item.kind
            )
        )
        .map((item) => item.value);
      plan.adviserDecision = {
        outcomeId: outcome.id,
        agenda: [...new Set([outcome.description, ...agenda])].slice(0, 4),
      };
    }
    return {
      plan,
      selectedTechniqueId,
      appointment,
      technique:
        SALES_TECHNIQUES.find((item) => item.id === selectedTechniqueId) ??
        null,
      supportedBenefits,
    };
  }

  resolveTurn(
    input: SalesPsychologyInput,
    brief: Pick<SalesReplyBrief, 'plan' | 'selectedTechniqueId'>,
    change: {
      understanding: SalesTurnUnderstanding;
      patch: BuyerContextPatch;
      answers: SalesAnswerCoverage;
      citedFactIds: readonly string[];
    }
  ): SalesTurnPlan {
    const questionIdentity = (understanding: SalesTurnUnderstanding) =>
      JSON.stringify(
        understanding.questions.map((question) => ({
          ...question,
          catalogScope: question.catalogScope ?? 'preferences',
          catalog: question.catalog ?? null,
        }))
      );
    if (
      input.understanding &&
      questionIdentity(input.understanding) !==
        questionIdentity(change.understanding)
    )
      throw new AssistantError('SALES_QUESTIONS_CHANGED', 502);
    // Validate newly interpreted statements independently. They enrich memory,
    // never retroactively change the instructions under which a reply was written.
    this.understandBuyer(input, change.patch, change.understanding);
    this.assertPlan(input, brief.plan, brief.selectedTechniqueId);
    const indexes = new Set(
      change.answers.map((answer) => answer.questionIndex)
    );
    if (
      indexes.size !== change.answers.length ||
      indexes.size !== change.understanding.questions.length ||
      [...indexes].some(
        (index) => index >= change.understanding.questions.length
      )
    )
      throw new AssistantError('SALES_ANSWER_COVERAGE_INCOMPLETE', 502);
    for (const answer of change.answers) {
      if (answer.status === 'read_required' || !answer.answer)
        throw new AssistantError('SALES_ANSWER_COVERAGE_INCOMPLETE', 502);
      if (
        answer.factIds.some((id) => !input.facts.some((fact) => fact.id === id))
      )
        throw new AssistantError('MODEL_CITATION_INVALID', 502);
    }
    if (
      change.citedFactIds.some(
        (id) => !input.facts.some((fact) => fact.id === id)
      )
    )
      throw new AssistantError('MODEL_CITATION_INVALID', 502);
    return {
      ...brief.plan,
      understanding: change.understanding,
      answers: change.answers,
      offeringIds: [...new Set(change.citedFactIds)].filter((id) =>
        input.facts.some((fact) => fact.id === id && isOfferingFact(fact))
      ),
    };
  }

  private assertPlan(
    input: SalesPsychologyInput,
    plan: SalesTurnPlan,
    selected: SalesTechniqueId | null
  ): void {
    const customerIds = new Set([
      ...input.messages
        .filter((message) => message.role === 'customer')
        .map((message) => message.id),
      ...input.buyer.observations.map((item) => item.evidence.messageId),
    ]);
    if (
      (selected !== null && !plan.eligibleTechniqueIds.includes(selected)) ||
      (selected === null && plan.eligibleTechniqueIds.length > 0) ||
      plan.eligibleTechniqueIds.some(
        (id) => !input.playbook.enabledTechniques.includes(id)
      ) ||
      plan.factRefs.some(
        (id) => !input.facts.some((fact) => fact.id === id && !fact.private)
      ) ||
      plan.buyerEvidenceRefs.some((id) => !customerIds.has(id)) ||
      (plan.invitation &&
        (plan.nextStep !== 'invitation' ||
          !input.playbook.adviserOutcomes.some(
            (outcome) => outcome.id === plan.invitation?.adviserOutcomeId
          )))
    )
      throw new AssistantError('SALES_PLAN_INVALID', 502);
  }

  applyTurn(
    input: SalesPsychologyInput,
    change: {
      patch: BuyerContextPatch;
      plan: SalesTurnPlan;
      selectedTechniqueId: SalesTechniqueId | null;
    }
  ): BuyerSalesContext {
    const buyer = this.understandBuyer(
      input,
      change.patch,
      change.plan.understanding
    );
    // The brief was validated against its pre-generation evidence in resolveTurn.
    // New buyer corrections may have removed an old observation since then.
    // Persist that correction without reinterpreting the delivered sales decision.
    if (
      (change.selectedTechniqueId === null &&
        change.plan.eligibleTechniqueIds.length > 0) ||
      (change.selectedTechniqueId !== null &&
        !change.plan.eligibleTechniqueIds.includes(
          change.selectedTechniqueId
        )) ||
      change.plan.eligibleTechniqueIds.some(
        (id) => !input.playbook.enabledTechniques.includes(id)
      )
    )
      throw new AssistantError('SALES_PLAN_INVALID', 502);
    let appointmentIntent = buyer.appointmentIntent;
    if (
      change.plan.understanding?.invitationResponse?.disposition === 'reopened'
    )
      appointmentIntent = 'unmentioned';
    if (
      change.plan.understanding?.invitationResponse?.disposition ===
        'not_now' ||
      change.plan.understanding?.invitationResponse?.disposition ===
        'coordinate'
    )
      appointmentIntent = 'deferred';
    else if (
      change.plan.understanding?.focus === 'decline' ||
      change.plan.understanding?.invitationResponse?.disposition ===
        'declined' ||
      (!change.plan.understanding &&
        (input.signal === 'appointment_refusal' ||
          input.signals?.readiness === 'declined'))
    )
      appointmentIntent = 'declined';
    else if (
      change.plan.understanding?.focus === 'arrange' ||
      (!change.plan.understanding &&
        (input.signal === 'appointment_request' ||
          input.signals?.readiness === 'requested'))
    )
      appointmentIntent = 'requested';
    else if (change.plan.objective === 'complete_scheduling')
      appointmentIntent = 'arranging';
    if (input.journey?.stage === 'crm_appointment_recorded')
      appointmentIntent = 'booked';
    const cancelled =
      input.journey?.stage === 'appointment_cancelled' &&
      change.plan.objective !== 'complete_scheduling';
    if (cancelled) appointmentIntent = 'unmentioned';
    return buyerSalesContextSchema.parse({
      ...buyer,
      appointmentIntent,
      ...(cancelled ? { appointmentSelection: null } : {}),
      ...(change.plan.adviserDecision
        ? { adviserDecision: change.plan.adviserDecision }
        : {}),
    });
  }
}
